import type { JSONCompatible, JSONValue } from '@direct-frontend/stdlib';

import { BaseError } from '../BaseError';
import { ErrorOptions } from '../../types/ErrorOptions';
import { createErrorInstanceOf } from './createErrorInstanceOf';
import { createErrorMarker } from './createErrorMarker';

/**
 * Важно вынести тип конструктора в алиас,
 * иначе modules-docgen не находит комментарий для результатов вызова createError
 * см. https://st.yandex-team.ru/DIRECT-193153
 */
type CustomError<
    Additional extends JSONCompatible<Additional>,
    GroupBy extends Record<string, string> | undefined,
> = new (message: string, options: ErrorOptions<Additional, GroupBy>) => BaseError<Additional, GroupBy>;

// Расширяет стандартный тип ошибки (но можно и руками написать класс)
export function createError<
    Additional extends JSONCompatible<Additional> = JSONValue,
    GroupBy extends Record<string, string> | undefined = Record<string, string> | undefined,
>(name: string): CustomError<Additional, GroupBy> {
    const CustomError = class extends BaseError<Additional, GroupBy> {
        public readonly name = name;

        constructor(message: string, options: ErrorOptions<Additional, GroupBy>) {
            super(message, options);
            Object.assign(this, { [createErrorMarker(name)]: true });
        }
    };

    Object.defineProperty(CustomError, Symbol.hasInstance, {
        value: createErrorInstanceOf(name),
        configurable: true,
    });

    Object.defineProperty(CustomError, 'name', {
        value: name,
    });

    return CustomError;
}
